import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPos } from '../../hooks/useAnchoredPos';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { formatLongDate } from '@packages/format';
import { ChevronLeftIcon } from './ChevronLeftIcon';
import { ChevronRightIcon } from './ChevronRightIcon';

/**
 * Estimated height in pixels of the CalendarPopover panel (header + 6-row day
 * grid + footer). Passed to useAnchoredPos so the popover flips above its
 * trigger when near the viewport bottom.
 */
export const CALENDAR_POPOVER_HEIGHT = 340;

/** Accessible name of the calendar dialog; portaled callers match on it. */
export const CALENDAR_DIALOG_LABEL = 'Select date';

// ---------------------------------------------------------------------------
// Locale-derived day-name abbreviations (en-ZA, Monday-first, UTC).
// 2024-01-01 is a known Monday; i=0..6 gives Mo..Su.
// ---------------------------------------------------------------------------
const DAY_NAMES = Array.from({ length: 7 }, (_, i) =>
  new Intl.DateTimeFormat('en-ZA', { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2024, 0, 1 + i)),
  ),
);

const MONTH_NAMES_SHORT = Array.from({ length: 12 }, (_, i) =>
  new Intl.DateTimeFormat('en-ZA', { month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2024, i, 1)),
  ),
);

/**
 * Returns the calendar grid rows for a given year/month.
 * Each row is an array of 7 day numbers (0 = empty cell).
 * Week starts on Monday (ISO 8601).
 *
 * Defensive: if either argument is NaN (which happens when a caller passes an
 * Invalid Date's getFullYear()/getMonth()), we fall back to the current month
 * rather than crashing with `new Array(NaN)` → RangeError.
 */
function buildCalendarGrid(year: number, month: number): number[][] {
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    // This is a programming error: the caller passed a malformed date.
    // Log and recover so the popover renders something sensible.
    console.warn('[CalendarPopover] buildCalendarGrid received non-finite year/month:', { year, month }, '– falling back to current month');
    const now = new Date();
    year = now.getFullYear();
    month = now.getMonth();
  }
  const firstDay = new Date(year, month, 1);
  // getDay() returns 0=Sun..6=Sat; convert to 0=Mon..6=Sun
  const startDow = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const grid: number[][] = [];
  let week: number[] = Array<number>(startDow).fill(0);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) {
      grid.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(0);
    grid.push(week);
  }
  return grid;
}

/** Formats YYYY-MM-DD from year/month(0-indexed)/day components. */
function toDayIso(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

type CalendarLevel = 'days' | 'months' | 'years';

export interface CalendarPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  /** ISO 8601 date string (YYYY-MM-DD) or null. */
  selectedDate: string | null;
  /** Called with YYYY-MM-DD when a day is picked. */
  onPick: (isoDate: string) => void;
  /** Whether a "Clear" action is shown (for the Due date chunk). */
  allowClear?: boolean;
  onClear?: () => void;
  onClose: () => void;
  /** Whether the associated mutation is in flight. */
  isPending?: boolean;
  /** Earliest selectable date in YYYY-MM-DD format (inclusive). */
  minDate?: string;
  /** Latest selectable date in YYYY-MM-DD format (inclusive). */
  maxDate?: string;
  /**
   * When true, only the 1st of each month is selectable.
   * All other day cells are rendered disabled and non-interactive.
   * Useful for the ledger opening-date picker where the 1st-of-month
   * constraint is enforced at the server level.
   */
  disableNonFirstOfMonth?: boolean;
  /** When true, only the last day of each month is selectable. */
  disableNonLastOfMonth?: boolean;
  /** Valid typed date previewed by CalendarPopoverButton without committing. */
  navigateToDate?: string | null;
  /**
   * Optional element whose subtree is treated as "inside" for outside-click
   * dismissal, in addition to the anchor. CalendarPopoverButton passes its
   * field wrapper so the in-field controls (e.g. the clear button) stay
   * interactive while the popover is open instead of being treated as an
   * outside click.
   */
  dismissExcludeRef?: React.RefObject<HTMLElement | null>;
  /**
   * Called instead of the default anchor-refocus when the popover closes while
   * focus was inside it (Escape / pick / keyboard nav). Lets the trigger return
   * focus to its input while suppressing its focus-opens-popover handler so the
   * popover does not immediately reopen. Omitted by callers whose trigger is a
   * plain button (no focus-opens behaviour) - they get the default refocus.
   */
  onReturnFocus?: () => void;
  /** Move focus into the panel on open. Input triggers retain focus when false. */
  autoFocus?: boolean;
}

export function CalendarPopover({
  anchorRef,
  selectedDate,
  onPick,
  allowClear,
  onClear,
  onClose,
  isPending,
  minDate,
  maxDate,
  disableNonFirstOfMonth = false,
  disableNonLastOfMonth = false,
  navigateToDate = null,
  dismissExcludeRef,
  onReturnFocus,
  autoFocus = true,
}: CalendarPopoverProps) {
  const pos = useAnchoredPos(anchorRef, 256, true, CALENDAR_POPOVER_HEIGHT);

  const trapRef = useFocusTrap(pos !== null, { autoFocus });

  // Track whether focus currently sits inside the popover panel. The unmount
  // handler below uses this to decide whether to return focus to the anchor:
  // only when focus was inside (Escape / pick / keyboard nav). When focus has
  // already moved elsewhere - a typed-Enter commit that advances to the next
  // field, a Tab to the next field, or an outside click - returning focus to
  // the anchor would fight the new location.
  const focusInsideRef = useRef(false);
  useEffect(() => {
    if (pos === null) return;
    const updateFocusInside = () => {
      focusInsideRef.current = trapRef.current?.contains(document.activeElement) ?? false;
    };
    updateFocusInside();
    document.addEventListener('focusin', updateFocusInside, true);
    return () => document.removeEventListener('focusin', updateFocusInside, true);
  }, [pos, trapRef]);

  // Hold the latest onReturnFocus in a ref so the unmount effect can stay
  // mounted-lifetime only (deps: [anchorRef]); depending on the callback
  // identity would re-run the effect every render and fire the cleanup's
  // refocus mid-render.
  const onReturnFocusRef = useRef(onReturnFocus);
  useEffect(() => {
    onReturnFocusRef.current = onReturnFocus;
  });

  // Return focus to the anchor on unmount, but only when focus was inside the
  // popover when it closed (see focusInsideRef). The parents conditionally
  // render this component (`{isOpen && <CalendarPopover />}`), so the close
  // transition always unmounts. Capture the anchor at effect-run time to avoid
  // reading a stale ref at unmount.
  useEffect(() => {
    const anchor = anchorRef.current;
    return () => {
      if (!focusInsideRef.current) return;
      const returnFocus = onReturnFocusRef.current;
      if (returnFocus) returnFocus();
      else anchor?.focus();
    };
  }, [anchorRef]);

  const today = useMemo(() => new Date(), []);
  const parsedSelected = selectedDate ? new Date(selectedDate) : null;
  const parsedNavigate = navigateToDate ? new Date(navigateToDate) : null;
  // Guard against Invalid Date (e.g. malformed selectedDate string): NaN is
  // not caught by `??` so we fall back explicitly.
  const parsedSelectedValid = parsedSelected !== null && Number.isFinite(parsedSelected.getTime()) ? parsedSelected : null;
  const parsedNavigateValid = parsedNavigate !== null && Number.isFinite(parsedNavigate.getTime()) ? parsedNavigate : null;

  const [viewYear, setViewYear] = useState(
    parsedSelectedValid?.getUTCFullYear() ?? today.getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState(
    parsedSelectedValid?.getUTCMonth() ?? today.getMonth(),
  );
  const [level, setLevel] = useState<CalendarLevel>('days');
  // Page offset for the year grid, in multiples of 12 years. Kept separate from
  // viewYear so paging the grid never moves the day-grid's target year or the
  // highlighted year. Reset to 0 whenever the year view is toggled or a year is
  // selected.
  const [yearPageOffset, setYearPageOffset] = useState(0);

  // ---------------------------------------------------------------------------
  // Roving tabindex: exactly one day button has tabIndex={0} at any time.
  // Initialise to: selected day (if in visible month) > today (if in visible month)
  // > first non-disabled day of the month.
  //
  // focusedDay is updated during render (not in a useEffect) to avoid stale
  // state after month navigation. We track the last view the focused day was
  // computed for; when the view changes, recompute synchronously.
  // ---------------------------------------------------------------------------
  const [focusedDay, setFocusedDay] = useState<number | null>(null);
  const [focusedDayView, setFocusedDayView] = useState<{ year: number; month: number } | null>(null);

  // Ref attached to the day-grid container. Used by the focus-restore effect below.
  const gridRef = useRef<HTMLDivElement>(null);
  const monthGridRef = useRef<HTMLDivElement>(null);
  const yearGridRef = useRef<HTMLDivElement>(null);
  const lastSyncedNavigateIsoRef = useRef<string | null>(null);
  // Set immediately before a focusedDay change driven by syncing the typed
  // preview (navigateToDate), consumed by the focus-restore effect below to
  // skip moving DOM focus for that one change. The operator's real focus is
  // still on the trigger input while they type - not inside this popover at
  // all - so calling `.focus()` on a day button there would silently steal it
  // away mid-keystroke: every further character typed after the preview
  // resolves to a full valid date would land on a day button (which ignores
  // it) rather than in the input, and a subsequent Tab keypress would never
  // reach the input's own leave-the-field handling because focus is no longer
  // there to receive it. Defaults to false so every other cause of a
  // focusedDay change (arrow keys, Home/End, PageUp/PageDown, paging the
  // month/year header) keeps moving DOM focus as before.
  const skipNextGridDomFocusRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Focus-restore effect: after every arrow-key navigation step, move DOM focus
  // to the button that now carries tabIndex={0}. This is distinct from the
  // useFocusTrap initial-mount focus; that fires once when the popover opens.
  // This effect fires on every subsequent focusedDay or focusedDayView change
  // so that document.activeElement tracks the roving-tabindex anchor.
  //
  // Guard: skip when a picker level is active (no day buttons rendered), when
  // focusedDay is null (no anchor to focus), or when the change that triggered
  // this run was the typed-preview sync (see skipNextGridDomFocusRef above).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (focusedDay === null || level !== 'days') return;
    if (skipNextGridDomFocusRef.current) {
      skipNextGridDomFocusRef.current = false;
      return;
    }
    // Input-triggered opening must not take focus from typing or a Tab destination.
    if (!autoFocus && !trapRef.current?.contains(document.activeElement)) return;
    const target = gridRef.current?.querySelector<HTMLButtonElement>('button[tabindex="0"]');
    target?.focus();
  }, [focusedDay, focusedDayView, level, autoFocus, trapRef]);

  // ---------------------------------------------------------------------------
  // Esc-to-close (capture phase). The component only renders when pos !== null,
  // so this effect mounts/unmounts naturally with visibility.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [onClose]);

  // ---------------------------------------------------------------------------
  // Outside-pointerdown-to-close (capture phase). Replaces a full-screen
  // backdrop: a backdrop sits over the anchor field and intercepts hover and
  // the first click on in-field controls (the typed-date clear button), so the
  // tooltip and pointer cursor never appear until the popover is dismissed.
  // A listener leaves the page interactive and still closes on a genuine
  // outside click. The panel, the anchor, and any dismissExcludeRef subtree
  // (the field wrapper, so in-field controls stay live) count as "inside".
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (target === null) return;
      if (trapRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      if (dismissExcludeRef?.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', handlePointerDown, { capture: true });
    return () => document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
  }, [onClose, anchorRef, dismissExcludeRef, trapRef]);

  // ---------------------------------------------------------------------------
  // minDate / maxDate validation and derived values for chevron clamping.
  // Guard: when minDate > maxDate (lexicographic, both YYYY-MM-DD) all days
  // would be disabled and keyboard navigation becomes a no-op. Log and ignore
  // both constraints so the calendar remains usable.
  // Guard: non-finite parsed years (malformed string) silently degrade the
  // same way as the defensive fallback in buildCalendarGrid.
  // ---------------------------------------------------------------------------
  const rawMinYear = minDate ? parseInt(minDate.slice(0, 4), 10) : null;
  const rawMaxYear = maxDate ? parseInt(maxDate.slice(0, 4), 10) : null;

  const constraintsInvalid =
    (rawMinYear !== null && !Number.isFinite(rawMinYear)) ||
    (rawMaxYear !== null && !Number.isFinite(rawMaxYear)) ||
    (minDate !== undefined && maxDate !== undefined && minDate > maxDate);

  if (constraintsInvalid) {
    console.warn('[CalendarPopover] minDate > maxDate or malformed constraint; ignoring constraints', { minDate, maxDate });
  }

  const effectiveMinDate = constraintsInvalid ? undefined : minDate;
  const effectiveMaxDate = constraintsInvalid ? undefined : maxDate;

  // Single source of truth for "is this day non-selectable": the 1st-of-month
  // constraint AND the min/max bounds. Used by the render loop, the click guard,
  // the roving-tabindex focus derivation, and every keyboard landing check, so a
  // disabled day can never receive the roving anchor or keyboard focus.
  function isDayDisabled(day: number, dayIso: string): boolean {
    const [year, month] = dayIso.split('-').map(Number);
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return (
      (disableNonFirstOfMonth && day !== 1) ||
      (disableNonLastOfMonth && day !== lastDay) ||
      (effectiveMinDate !== undefined && dayIso < effectiveMinDate) ||
      (effectiveMaxDate !== undefined && dayIso > effectiveMaxDate)
    );
  }

  function isMonthDisabled(year: number, monthIdx: number): boolean {
    const firstIso = toDayIso(year, monthIdx, 1);
    if (disableNonFirstOfMonth) {
      return isDayDisabled(1, firstIso);
    }

    const lastDay = new Date(year, monthIdx + 1, 0).getDate();
    const lastIso = toDayIso(year, monthIdx, lastDay);
    if (disableNonLastOfMonth) {
      return isDayDisabled(lastDay, lastIso);
    }
    return (
      (effectiveMinDate !== undefined && lastIso < effectiveMinDate) ||
      (effectiveMaxDate !== undefined && firstIso > effectiveMaxDate)
    );
  }

  // Resolve the day a min/max boundary clamp should land the roving anchor on,
  // so PageUp/PageDown (and their Shift+ variants) never strand tabIndex={0} on
  // a disabled day. Prefer the boundary day itself; if it is disabled (e.g. the
  // 1st-of-month constraint with a non-first minDate/maxDate) snap to the 1st,
  // the only other selectable candidate. Returns null when even the 1st is
  // disabled, signalling the caller to leave focus in place.
  function boundaryLandingDay(boundaryDay: number, year: number, month: number): number | null {
    if (!isDayDisabled(boundaryDay, toDayIso(year, month, boundaryDay))) return boundaryDay;
    if (!isDayDisabled(1, toDayIso(year, month, 1))) return 1;
    return null;
  }

  const minYear = effectiveMinDate ? parseInt(effectiveMinDate.slice(0, 4), 10) : null;
  const minMonthIdx = effectiveMinDate ? parseInt(effectiveMinDate.slice(5, 7), 10) - 1 : null;
  const maxYear = effectiveMaxDate ? parseInt(effectiveMaxDate.slice(0, 4), 10) : null;
  const maxMonthIdx = effectiveMaxDate ? parseInt(effectiveMaxDate.slice(5, 7), 10) - 1 : null;

  const isPrevMonthDisabled =
    minYear !== null &&
    minMonthIdx !== null &&
    (viewYear < minYear || (viewYear === minYear && viewMonth <= minMonthIdx));

  const isNextMonthDisabled =
    maxYear !== null &&
    maxMonthIdx !== null &&
    (viewYear > maxYear || (viewYear === maxYear && viewMonth >= maxMonthIdx));

  const isPrevYearDisabled = minYear !== null && viewYear <= minYear;
  const isNextYearDisabled = maxYear !== null && viewYear >= maxYear;

  function prevMonth() {
    if (isPrevMonthDisabled) return;
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  }

  function nextMonth() {
    if (isNextMonthDisabled) return;
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  }

  function prevYear() {
    if (isPrevYearDisabled) return;
    setViewYear((y) => y - 1);
  }

  function nextYear() {
    if (isNextYearDisabled) return;
    setViewYear((y) => y + 1);
  }

  function handleDayClick(day: number) {
    if (day === 0) return;
    const dayIso = toDayIso(viewYear, viewMonth, day);
    if (isDayDisabled(day, dayIso)) return;
    onPick(dayIso);
    onClose();
  }

  function handleTodayClick() {
    const t = new Date();
    // When a month boundary is required, snap "Today" to that boundary so the
    // footer action remains useful.
    const day = disableNonFirstOfMonth
      ? 1
      : disableNonLastOfMonth
        ? new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate()
        : t.getDate();
    const mm = String(t.getMonth() + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const todayIso = `${t.getFullYear()}-${mm}-${dd}`;
    const targetIso = isDayDisabled(day, todayIso) ? effectiveMaxDate : todayIso;
    if (targetIso === undefined) return;
    onPick(targetIso);
    onClose();
  }

  const grid = buildCalendarGrid(viewYear, viewMonth);

  // ---------------------------------------------------------------------------
  // focusedDay initialisation: synchronous derived-state pattern (no useEffect).
  // When the visible month changes, recompute the focused day immediately during
  // render so useFocusTrap auto-focuses the correct button on the next paint.
  //
  // Priority: selected day (if in visible month) > today (if in visible month)
  // > first non-disabled day of the month.
  // ---------------------------------------------------------------------------
  const daysInViewMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  if (parsedNavigateValid) {
    const navigateYear = parsedNavigateValid.getUTCFullYear();
    const navigateMonth = parsedNavigateValid.getUTCMonth();
    const navigateDay = parsedNavigateValid.getUTCDate();
    const navigateIso = toDayIso(navigateYear, navigateMonth, navigateDay);
    const shouldSyncNavigateView = lastSyncedNavigateIsoRef.current !== navigateIso;

    if (shouldSyncNavigateView) {
      lastSyncedNavigateIsoRef.current = navigateIso;
      skipNextGridDomFocusRef.current = true;
      setViewYear(navigateYear);
      setViewMonth(navigateMonth);
      setLevel('days');
      setFocusedDay(navigateDay);
      setFocusedDayView({ year: navigateYear, month: navigateMonth });
    }
  } else if (lastSyncedNavigateIsoRef.current !== null) {
    lastSyncedNavigateIsoRef.current = null;
  }

  const viewChanged =
    focusedDayView === null ||
    focusedDayView.year !== viewYear ||
    focusedDayView.month !== viewMonth;

  if (viewChanged) {
    let newFocused: number | null = null;

    // 1. Typed preview day in this month (only if it is itself selectable)
    const previewUtcDate = parsedNavigateValid ? parsedNavigateValid.getUTCDate() : null;
    if (
      parsedNavigateValid &&
      previewUtcDate !== null &&
      parsedNavigateValid.getUTCFullYear() === viewYear &&
      parsedNavigateValid.getUTCMonth() === viewMonth &&
      !isDayDisabled(previewUtcDate, toDayIso(viewYear, viewMonth, previewUtcDate))
    ) {
      newFocused = previewUtcDate;
    } else {
      // 2. Selected day in this month (only if it is itself selectable)
      const selectedUtcDate = parsedSelectedValid ? parsedSelectedValid.getUTCDate() : null;
      if (
        parsedSelectedValid &&
        selectedUtcDate !== null &&
        parsedSelectedValid.getUTCFullYear() === viewYear &&
        parsedSelectedValid.getUTCMonth() === viewMonth &&
        !isDayDisabled(selectedUtcDate, toDayIso(viewYear, viewMonth, selectedUtcDate))
      ) {
        newFocused = selectedUtcDate;
      } else if (
        // 3. Today in this month, only if today is selectable (use the stable
        // `today` memo to keep render pure)
        today.getFullYear() === viewYear &&
        today.getMonth() === viewMonth &&
        !isDayDisabled(today.getDate(), toDayIso(viewYear, viewMonth, today.getDate()))
      ) {
        newFocused = today.getDate();
      } else {
        // 4. First non-disabled day of the month
        for (let d = 1; d <= daysInViewMonth; d++) {
          const iso = toDayIso(viewYear, viewMonth, d);
          if (!isDayDisabled(d, iso)) {
            newFocused = d;
            break;
          }
        }
      }
    }

    setFocusedDay(newFocused);
    setFocusedDayView({ year: viewYear, month: viewMonth });
  }

  // ---------------------------------------------------------------------------
  // Arrow-key navigation for the day grid (ARIA grid pattern).
  // ---------------------------------------------------------------------------
  function handleGridKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (level !== 'days') return;
    if (focusedDay === null) return;

    const ARROWS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (ARROWS.includes(e.key)) {
      e.preventDefault();

      let offset = 0;
      if (e.key === 'ArrowLeft') offset = -1;
      else if (e.key === 'ArrowRight') offset = 1;
      else if (e.key === 'ArrowUp') offset = -7;
      else if (e.key === 'ArrowDown') offset = 7;

      const target = focusedDay + offset;

      if (target < 1) {
        // Went before the first day of this month
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          // Advance to previous month
          let prevMonthYear = viewYear;
          let prevMonthIdx = viewMonth - 1;
          if (prevMonthIdx < 0) {
            prevMonthIdx = 11;
            prevMonthYear = viewYear - 1;
          }
          const lastDayOfPrev = new Date(prevMonthYear, prevMonthIdx + 1, 0).getDate();
          const landingDay = e.key === 'ArrowLeft' ? lastDayOfPrev : lastDayOfPrev + target;
          const clampedDay = Math.max(1, Math.min(landingDay, lastDayOfPrev));
          const candidateIso = toDayIso(prevMonthYear, prevMonthIdx, clampedDay);
          if (isDayDisabled(clampedDay, candidateIso)) return;
          // Only advance if prevMonth is not disabled
          if (!isPrevMonthDisabled) {
            setViewYear(prevMonthYear);
            setViewMonth(prevMonthIdx);
            setFocusedDay(clampedDay);
            // Update focusedDayView so the derived-state block does not override
            // the focused day we are explicitly setting here.
            setFocusedDayView({ year: prevMonthYear, month: prevMonthIdx });
          }
        }
        return;
      }

      if (target > daysInViewMonth) {
        // Went past the last day of this month
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          // Advance to next month
          let nextMonthYear = viewYear;
          let nextMonthIdx = viewMonth + 1;
          if (nextMonthIdx > 11) {
            nextMonthIdx = 0;
            nextMonthYear = viewYear + 1;
          }
          const landingDay = e.key === 'ArrowRight' ? 1 : target - daysInViewMonth;
          const lastDayOfNext = new Date(nextMonthYear, nextMonthIdx + 1, 0).getDate();
          const clampedDay = Math.max(1, Math.min(landingDay, lastDayOfNext));
          const candidateIso = toDayIso(nextMonthYear, nextMonthIdx, clampedDay);
          if (isDayDisabled(clampedDay, candidateIso)) return;
          if (!isNextMonthDisabled) {
            setViewYear(nextMonthYear);
            setViewMonth(nextMonthIdx);
            setFocusedDay(clampedDay);
            // Update focusedDayView so the derived-state block does not override
            // the focused day we are explicitly setting here.
            setFocusedDayView({ year: nextMonthYear, month: nextMonthIdx });
          }
        }
        return;
      }

      // Target is within the current month; skip if it is not selectable
      const candidateIso = toDayIso(viewYear, viewMonth, target);
      if (isDayDisabled(target, candidateIso)) return;

      setFocusedDay(target);
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDayClick(focusedDay);
      return;
    }

    // -------------------------------------------------------------------------
    // WAI-ARIA extended keyboard shortcuts (ARIA Date Picker pattern):
    //   Home     → first day of the current week (column 0 of focusedDay's row)
    //   End      → last day of the current week (column 6 of focusedDay's row)
    //   PageUp   → same day-of-month in the previous month (or closest valid day)
    //             Shift+PageUp → previous year
    //   PageDown → same day-of-month in the next month (or closest valid day)
    //             Shift+PageDown → next year
    // All prevent page scroll. Respect effectiveMinDate / effectiveMaxDate.
    // -------------------------------------------------------------------------
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      // Determine which column (0=Mon..6=Sun) focusedDay falls in.
      // focusedDay is 1-indexed; find its row in the grid and read col index.
      const dayOfWeek = new Date(viewYear, viewMonth, focusedDay).getDay(); // 0=Sun..6=Sat
      const colIdx = (dayOfWeek + 6) % 7; // 0=Mon..6=Sun
      const targetDay = e.key === 'Home' ? focusedDay - colIdx : focusedDay + (6 - colIdx);
      const clampedDay = Math.max(1, Math.min(targetDay, daysInViewMonth));
      const candidateIso = toDayIso(viewYear, viewMonth, clampedDay);
      if (isDayDisabled(clampedDay, candidateIso)) return;
      setFocusedDay(clampedDay);
      return;
    }

    if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      const direction = e.key === 'PageUp' ? -1 : 1;

      if (e.shiftKey) {
        // Shift+PageUp/Down: jump one year
        const targetYear = viewYear + direction;
        const lastDayOfTargetMonth = new Date(targetYear, viewMonth + 1, 0).getDate();
        const landingDay = Math.min(focusedDay, lastDayOfTargetMonth);
        const candidateIso = toDayIso(targetYear, viewMonth, landingDay);
        // Respect year-level constraints
        if (effectiveMinDate && candidateIso < effectiveMinDate) {
          // Try to land on the minDate day if it falls in this month of targetYear
          const minYear2 = parseInt(effectiveMinDate.slice(0, 4), 10);
          const minMonthIdx2 = parseInt(effectiveMinDate.slice(5, 7), 10) - 1;
          const minDay = parseInt(effectiveMinDate.slice(8, 10), 10);
          if (targetYear === minYear2 && viewMonth === minMonthIdx2) {
            // minDay may itself be disabled (1st-of-month constraint with a
            // non-first minDate); snap to the only selectable day (the 1st),
            // or leave focus in place if even that is disabled.
            const landingTarget = boundaryLandingDay(minDay, targetYear, viewMonth);
            if (landingTarget === null) return;
            setViewYear(targetYear);
            setFocusedDay(landingTarget);
            setFocusedDayView({ year: targetYear, month: viewMonth });
          }
          return;
        }
        if (effectiveMaxDate && candidateIso > effectiveMaxDate) {
          const maxYear2 = parseInt(effectiveMaxDate.slice(0, 4), 10);
          const maxMonthIdx2 = parseInt(effectiveMaxDate.slice(5, 7), 10) - 1;
          const maxDay = parseInt(effectiveMaxDate.slice(8, 10), 10);
          if (targetYear === maxYear2 && viewMonth === maxMonthIdx2) {
            const landingTarget = boundaryLandingDay(maxDay, targetYear, viewMonth);
            if (landingTarget === null) return;
            setViewYear(targetYear);
            setFocusedDay(landingTarget);
            setFocusedDayView({ year: targetYear, month: viewMonth });
          }
          return;
        }
        // landingDay clears min/max but may still be disabled by the
        // 1st-of-month constraint; snap to the only selectable day (the 1st).
        if (isDayDisabled(landingDay, candidateIso)) {
          const firstIso = toDayIso(targetYear, viewMonth, 1);
          if (isDayDisabled(1, firstIso)) return;
          setViewYear(targetYear);
          setFocusedDay(1);
          setFocusedDayView({ year: targetYear, month: viewMonth });
          return;
        }
        setViewYear(targetYear);
        setFocusedDay(landingDay);
        setFocusedDayView({ year: targetYear, month: viewMonth });
        return;
      }

      // PageUp/Down: jump one month
      let targetMonthYear = viewYear;
      let targetMonthIdx = viewMonth + direction;
      if (targetMonthIdx < 0) {
        targetMonthIdx = 11;
        targetMonthYear = viewYear - 1;
      } else if (targetMonthIdx > 11) {
        targetMonthIdx = 0;
        targetMonthYear = viewYear + 1;
      }
      const lastDayOfTarget = new Date(targetMonthYear, targetMonthIdx + 1, 0).getDate();
      const landingDay = Math.min(focusedDay, lastDayOfTarget);
      const candidateIso = toDayIso(targetMonthYear, targetMonthIdx, landingDay);
      if (effectiveMinDate && candidateIso < effectiveMinDate) {
        // Try landing on minDate day if it's in this target month
        const minYear2 = parseInt(effectiveMinDate.slice(0, 4), 10);
        const minMonthIdx2 = parseInt(effectiveMinDate.slice(5, 7), 10) - 1;
        const minDay = parseInt(effectiveMinDate.slice(8, 10), 10);
        if (targetMonthYear === minYear2 && targetMonthIdx === minMonthIdx2) {
          // minDay may itself be disabled (1st-of-month constraint with a
          // non-first minDate); snap to the only selectable day (the 1st),
          // or leave focus in place if even that is disabled.
          const landingTarget = boundaryLandingDay(minDay, targetMonthYear, targetMonthIdx);
          if (landingTarget === null) return;
          setViewYear(targetMonthYear);
          setViewMonth(targetMonthIdx);
          setFocusedDay(landingTarget);
          setFocusedDayView({ year: targetMonthYear, month: targetMonthIdx });
        }
        return;
      }
      if (effectiveMaxDate && candidateIso > effectiveMaxDate) {
        const maxYear2 = parseInt(effectiveMaxDate.slice(0, 4), 10);
        const maxMonthIdx2 = parseInt(effectiveMaxDate.slice(5, 7), 10) - 1;
        const maxDay = parseInt(effectiveMaxDate.slice(8, 10), 10);
        if (targetMonthYear === maxYear2 && targetMonthIdx === maxMonthIdx2) {
          const landingTarget = boundaryLandingDay(maxDay, targetMonthYear, targetMonthIdx);
          if (landingTarget === null) return;
          setViewYear(targetMonthYear);
          setViewMonth(targetMonthIdx);
          setFocusedDay(landingTarget);
          setFocusedDayView({ year: targetMonthYear, month: targetMonthIdx });
        }
        return;
      }
      // landingDay clears min/max but may still be disabled by the
      // 1st-of-month constraint; snap to the only selectable day (the 1st).
      if (isDayDisabled(landingDay, candidateIso)) {
        const firstIso = toDayIso(targetMonthYear, targetMonthIdx, 1);
        if (isDayDisabled(1, firstIso)) return;
        setViewYear(targetMonthYear);
        setViewMonth(targetMonthIdx);
        setFocusedDay(1);
        setFocusedDayView({ year: targetMonthYear, month: targetMonthIdx });
        return;
      }
      setViewYear(targetMonthYear);
      setViewMonth(targetMonthIdx);
      setFocusedDay(landingDay);
      setFocusedDayView({ year: targetMonthYear, month: targetMonthIdx });
    }
  }

  function handlePickerGridKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const keyOffsets: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -4,
      ArrowDown: 4,
    };

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (document.activeElement instanceof HTMLButtonElement) {
        document.activeElement.click();
      }
      return;
    }

    const offset = keyOffsets[e.key];
    if (offset === undefined) return;
    e.preventDefault();

    const buttons = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'),
    );
    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (currentIndex === -1) return;

    const nextIndex = Math.max(0, Math.min(currentIndex + offset, buttons.length - 1));
    buttons[nextIndex]?.focus();
  }

  const monthOnlyLabel = new Intl.DateTimeFormat('en-ZA', {
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(viewYear, viewMonth, 1)));

  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDay = today.getDate();

  const selectedDay = parsedSelectedValid ? parsedSelectedValid.getUTCDate() : null;
  const selectedYear = parsedSelectedValid ? parsedSelectedValid.getUTCFullYear() : null;
  const selectedMonth = parsedSelectedValid ? parsedSelectedValid.getUTCMonth() : null;
  const previewDay = parsedNavigateValid ? parsedNavigateValid.getUTCDate() : null;
  const previewYear = parsedNavigateValid ? parsedNavigateValid.getUTCFullYear() : null;
  const previewMonth = parsedNavigateValid ? parsedNavigateValid.getUTCMonth() : null;

  // ---------------------------------------------------------------------------
  // Year-picker grid: 12 years, centred around viewYear when the page offset is
  // 0 (viewYear-5 .. viewYear+6), shifted by 12 years per page step otherwise.
  // ---------------------------------------------------------------------------
  const yearGridStart = viewYear - 5 + yearPageOffset * 12;
  const yearGridEnd = viewYear + 6 + yearPageOffset * 12;
  const yearGridYears = Array.from({ length: 12 }, (_, i) => yearGridStart + i);
  // Year-view header shows the range, e.g. "2021 – 2032" (en dash + spaces)
  const yearRangeLabel = `${yearGridStart} – ${yearGridEnd}`;

  // Disable year-page paging once the visible window's edge reaches the min/max
  // constraint: an earlier/later page would contain only out-of-range years.
  const isPrevYearPageDisabled = minYear !== null && yearGridStart <= minYear;
  const isNextYearPageDisabled = maxYear !== null && yearGridEnd >= maxYear;

  function prevYearPage() {
    if (isPrevYearPageDisabled) return;
    setYearPageOffset((o) => o - 1);
  }

  function nextYearPage() {
    if (isNextYearPageDisabled) return;
    setYearPageOffset((o) => o + 1);
  }

  function handleYearSelect(yr: number) {
    setViewYear(yr);
    setYearPageOffset(0);
    setLevel('months');
  }

  function handleMonthSelect(monthIdx: number) {
    if (isMonthDisabled(viewYear, monthIdx)) return;
    setViewMonth(monthIdx);
    setLevel('days');
  }

  function zoomOutOneLevel() {
    setYearPageOffset(0);
    setLevel((current) => {
      if (current === 'days') return 'months';
      if (current === 'months') return 'years';
      return current;
    });
  }

  const previousAction = level === 'years' ? prevYearPage : level === 'months' ? prevYear : prevMonth;
  const nextAction = level === 'years' ? nextYearPage : level === 'months' ? nextYear : nextMonth;
  const isPreviousDisabled =
    level === 'years'
      ? isPrevYearPageDisabled
      : level === 'months'
        ? isPrevYearDisabled
        : isPrevMonthDisabled;
  const isNextDisabled =
    level === 'years'
      ? isNextYearPageDisabled
      : level === 'months'
        ? isNextYearDisabled
        : isNextMonthDisabled;
  const previousLabel =
    level === 'years' ? 'Previous years' : level === 'months' ? 'Previous year' : 'Previous month';
  const nextLabel = level === 'years' ? 'Next years' : level === 'months' ? 'Next year' : 'Next month';

  // Shared style for the header breadcrumb buttons so the month, year, and the
  // month/year-level labels all read as equal, tappable controls.
  const headerButtonClass =
    'rounded-md px-2 py-1 text-secondary transition-colors hover:bg-bg-muted hover:text-primary';

  useEffect(() => {
    if (level === 'months') {
      const target =
        monthGridRef.current?.querySelector<HTMLButtonElement>('button[data-selected="true"]:not(:disabled)') ??
        monthGridRef.current?.querySelector<HTMLButtonElement>('button[data-current="true"]:not(:disabled)') ??
        monthGridRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)');
      target?.focus();
    }
    if (level === 'years') {
      const target =
        yearGridRef.current?.querySelector<HTMLButtonElement>('button[data-selected="true"]:not(:disabled)') ??
        yearGridRef.current?.querySelector<HTMLButtonElement>('button[data-current="true"]:not(:disabled)') ??
        yearGridRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)');
      target?.focus();
    }
  }, [level, viewMonth, viewYear, yearGridStart]);

  if (!pos) return null;

  return createPortal(
    <>
      <div
        ref={trapRef}
        style={{ top: pos.top, left: pos.left }}
        // z-[10000]: above the z-[9999] popovers (SearchPopover, ActionMenu,
        // ReportRangeControl's panel) so a DateFilterInput opened from inside
        // one of those still renders its calendar on top rather than behind it.
        className="fixed z-[10000] w-64 rounded-xl border border-border bg-bg-primary shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={CALENDAR_DIALOG_LABEL}
      >
        {/* Calendar navigation */}
        <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
          <button
            type="button"
            onClick={previousAction}
            disabled={isPreviousDisabled}
            aria-label={previousLabel}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-text-muted transition-colors hover:bg-bg-muted hover:text-secondary disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0 sm:min-w-0 sm:h-7 sm:w-7"
          >
            <ChevronLeftIcon className="h-3.5 w-3.5" />
          </button>
          <div className="flex min-w-0 items-center gap-1 text-sm font-medium">
            {level === 'days' && (
              <>
                <button
                  type="button"
                  onClick={zoomOutOneLevel}
                  aria-label={`Select month, currently ${monthOnlyLabel}`}
                  className={headerButtonClass}
                >
                  {monthOnlyLabel}
                </button>
                <span className="h-4 w-px bg-border" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => {
                    setYearPageOffset(0);
                    setLevel('years');
                  }}
                  aria-label={`Select year, currently ${viewYear}`}
                  className={headerButtonClass}
                >
                  {viewYear}
                </button>
              </>
            )}
            {level === 'months' && (
              <button
                type="button"
                onClick={zoomOutOneLevel}
                aria-label={`Select year, currently ${viewYear}`}
                className={headerButtonClass}
              >
                {viewYear}
              </button>
            )}
            {level === 'years' && (
              <button
                type="button"
                onClick={() => setLevel('months')}
                aria-label={`Year range ${yearRangeLabel}, return to month view`}
                className={headerButtonClass}
              >
                {yearRangeLabel}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={nextAction}
            disabled={isNextDisabled}
            aria-label={nextLabel}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-text-muted transition-colors hover:bg-bg-muted hover:text-secondary disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0 sm:min-w-0 sm:h-7 sm:w-7"
          >
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="p-2">
          {level === 'years' ? (
            /* Year-picker grid: 4 columns × 3 rows */
            <div
              ref={yearGridRef}
              className="grid gap-1"
              style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
              onKeyDown={handlePickerGridKeyDown}
            >
              {yearGridYears.map((yr) => {
                const isYearDisabled =
                  (minYear !== null && yr < minYear) ||
                  (maxYear !== null && yr > maxYear);
                const isSelectedYear = yr === selectedYear;
                const isCurrentYear = yr === viewYear;
                return (
                  <button
                    key={yr}
                    type="button"
                    disabled={isYearDisabled}
                    data-current={isCurrentYear}
                    data-selected={isSelectedYear}
                    onClick={() => handleYearSelect(yr)}
                    className={`rounded-md py-1.5 text-xs font-medium transition-transform duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-40 ${
                      isSelectedYear
                        ? 'bg-primary text-white'
                        : isCurrentYear
                          ? 'text-secondary ring-1 ring-primary hover:bg-bg-muted'
                          : 'text-secondary hover:bg-bg-muted'
                    }`}
                  >
                    {yr}
                  </button>
                );
              })}
            </div>
          ) : level === 'months' ? (
            <div
              ref={monthGridRef}
              className="grid gap-1"
              style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
              onKeyDown={handlePickerGridKeyDown}
            >
              {MONTH_NAMES_SHORT.map((monthName, monthIdx) => {
                const isDisabled = isMonthDisabled(viewYear, monthIdx);
                const isSelectedMonth = selectedYear === viewYear && selectedMonth === monthIdx;
                const isCurrentMonth = viewMonth === monthIdx;
                return (
                  <button
                    key={monthName}
                    type="button"
                    disabled={isDisabled}
                    data-current={isCurrentMonth}
                    data-selected={isSelectedMonth}
                    onClick={() => handleMonthSelect(monthIdx)}
                    className={`min-h-[44px] rounded-md py-1.5 text-xs font-medium transition-transform duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0 ${
                      isSelectedMonth
                        ? 'bg-primary text-white'
                        : isCurrentMonth
                          ? 'text-secondary ring-1 ring-primary hover:bg-bg-muted'
                          : 'text-secondary hover:bg-bg-muted'
                    }`}
                  >
                    {monthName}
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              {/* Day name headers */}
              <div
                className="mb-1 grid gap-0.5"
                style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
              >
                {DAY_NAMES.map((d) => (
                  <div
                    key={d}
                    className="flex h-7 items-center justify-center text-[10px] font-semibold uppercase tracking-wider text-text-muted"
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* Day grid: ARIA grid pattern with roving tabindex */}
              <div
                ref={gridRef}
                role="grid"
                className="grid gap-0.5"
                style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
                onKeyDown={handleGridKeyDown}
              >
                {grid.map((week, wi) =>
                  week.map((day, di) => {
                    if (day === 0) {
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      return <div key={`${wi}-${di}`} className="h-7 w-7" />;
                    }
                    const dayIso = toDayIso(viewYear, viewMonth, day);
                    const isDisabled = isDayDisabled(day, dayIso);
                    const isSelected =
                      selectedDay === day &&
                      selectedYear === viewYear &&
                      selectedMonth === viewMonth;
                    const isToday =
                      todayDay === day &&
                      todayYear === viewYear &&
                      todayMonth === viewMonth;
                    const isPreviewed =
                      previewDay === day &&
                      previewYear === viewYear &&
                      previewMonth === viewMonth &&
                      !isSelected;
                    // Roving tabindex: only the focused day gets tabIndex={0}
                    const tabIdx = focusedDay === day ? 0 : -1;
                    return (
                      <button key={day}
                        type="button"
                        disabled={isDisabled || isPending}
                        tabIndex={tabIdx}
                        onClick={() => handleDayClick(day)}
                        onFocus={() => setFocusedDay(day)}
                        aria-label={formatLongDate(dayIso)}
                        className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-xs transition-colors sm:min-h-0 sm:min-w-0 sm:h-7 sm:w-7 ${
                          isDisabled
                            ? 'cursor-not-allowed opacity-40'
                            : isSelected
                              ? 'bg-primary font-semibold text-white'
                              : isPreviewed
                                ? 'font-semibold text-primary ring-1 ring-primary'
                              : isToday
                                ? 'font-medium text-secondary ring-1 ring-primary'
                                : 'text-secondary hover:bg-bg-muted'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  }),
                )}
              </div>
            </>
          )}

          {/* Footer actions */}
          <div className="mt-2 border-t border-border pt-2">
            <button
              type="button"
              onClick={handleTodayClick}
              disabled={isPending}
              className="w-full rounded-lg py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/5"
            >
              Today
            </button>
            {allowClear && onClear && selectedDate && (
              <button
                type="button"
                onClick={() => {
                  onClear();
                  onClose();
                }}
                disabled={isPending}
                className="mt-1 w-full rounded-lg py-1 text-xs font-medium text-text-muted transition-colors hover:bg-bg-muted hover:text-secondary"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
